import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import billingService from '../../api/billingService';
import { formatCurrency, formatDate } from '../../utils/formatters';

const SubscriptionView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSubscription();
  }, [id]);

  const fetchSubscription = async () => {
    try {
      const res = await billingService.getSubscriptionById(id);
      if (res.success) setSubscription(res.data);
    } catch (error) {
      console.error('Error fetching subscription details:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="p-5 text-center">Loading subscription details...</div>;
  if (!subscription) return <div className="p-5 text-center text-danger">Subscription not found.</div>;

  const customerName = [
    subscription.customer?.firstname || subscription.customerData?.firstname || '',
    subscription.customer?.lastname || subscription.customerData?.lastname || ''
  ].join(' ').trim()
    || subscription.customer?.contactPerson
    || subscription.customerData?.contactPerson
    || subscription.customer?.customerName
    || subscription.customerData?.customerName
    || subscription.customer?.company
    || subscription.customerData?.company
    || 'Unknown Customer';

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
         
          <h4 className="fw-700 mb-0">{subscription.custom_alias || subscription.product?.productName}</h4>
          <p className="text-muted small mb-0">ID: {subscription._id}</p>
        </div>
        <div className="d-flex gap-2">
          <span className={`badge badge-soft-${subscription.status === 'active' ? 'success' : subscription.status === 'expired' ? 'danger' : 'warning'} align-self-center me-2`}>
            {subscription.status?.toUpperCase()}
          </span>
          <button className="btn btn-primary px-4" onClick={() => navigate(`/billing/subscriptions/edit/${subscription._id}`)}>
            <i className="bi bi-pencil me-2"></i> Edit Subscription
          </button>
        </div>
      </div>

      <div className="row g-4">
        <div className="col-md-8">
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-header bg-transparent border-0 pt-4 px-4">
              <h6 className="fw-700 mb-0">Service Details</h6>
            </div>
            <div className="card-body p-4">
              <div className="row g-4">
                <div className="col-md-6">
                  <label className="text-muted text-xs fw-600 mb-1 d-block uppercase">CUSTOMER</label>
                  <div className="fw-600 text-primary cursor-pointer" onClick={() => navigate(`/billing/customers/${subscription.customer_id}`)}>
                    {customerName}
                  </div>
                </div>
                <div className="col-md-6">
                  <label className="text-muted text-xs fw-600 mb-1 d-block uppercase">PRODUCT</label>
                  <div className="fw-600">{subscription.product?.productName}</div>
                </div>
                <div className="col-md-4">
                  <label className="text-muted text-xs fw-600 mb-1 d-block uppercase">SALES DATE</label>
                  <div className="fw-600">{subscription.sales_date ? formatDate(subscription.sales_date) : 'N/A'}</div>
                </div>
                <div className="col-md-4">
                  <label className="text-muted text-xs fw-600 mb-1 d-block uppercase">START DATE</label>
                  <div className="fw-600">{formatDate(subscription.start_date)}</div>
                </div>
                <div className="col-md-4">
                  <label className="text-muted text-xs fw-600 mb-1 d-block uppercase">EXPIRY DATE</label>
                  <div className="fw-600 text-danger">{formatDate(subscription.next_renewal_date)}</div>
                </div>
                <div className="col-12">
                  <label className="text-muted text-xs fw-600 mb-1 d-block uppercase">TECHNICAL / SERVICE DETAILS</label>
                  <div className="p-3 bg-light rounded-3 text-sm" style={{ whiteSpace: 'pre-line' }}>
                    {subscription.service_details || 'No additional details provided.'}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="card border-0 shadow-sm">
            <div className="card-header bg-transparent border-0 pt-4 px-4">
              <h6 className="fw-700 mb-0">Update History</h6>
            </div>
            <div className="card-body p-4">
              <div className="timeline-sm">
                {subscription.history?.length > 0 ? subscription.history.slice().reverse().map((h, i) => {
                  const isRenewal = h?.isRenewal;
                  const isNewSale = !isRenewal && i === (subscription.history.length - 1); // Last entry in original array is the first one
                  
                  return (
                    <div key={i} className="mb-4 pb-3 border-bottom last-child-no-border">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <div>
                          {isRenewal ? (
                            <span className="badge bg-info text-white me-2">RENEWAL</span>
                          ) : isNewSale ? (
                            <span className="badge bg-success text-white me-2">NEW SALE</span>
                          ) : (
                            <span className="badge bg-light text-dark border me-2">UPDATE</span>
                          )}
                          <span className="text-xs fw-700">{h.updated_by}</span>
                        </div>
                        <span className="text-xs text-muted">{formatDate(h.updated_at)}</span>
                      </div>

                      <div className="ps-3 border-start border-2">
                        <div className="text-xs text-muted mb-2">
                          Status:  <span className="text-primary fw-600">{h?.status || subscription.status}</span>
                        </div>

                        {h?.next_renewal_date && (isRenewal || h?.next_renewal_date !== h?.next_renewal_date) && (
                          <div className="text-xs text-muted mb-2">
                            <i className="bi bi-calendar-event me-1"></i>
                            Expiry: <span className="text-dark fw-600">{formatDate(h.next_renewal_date)}</span>
                          </div>
                        )}

                        {h?.service_details && (
                          <div className="mb-2">
                            <label className="text-muted text-xs fw-600 d-block uppercase mb-1">Service Details</label>
                            <div className="text-xs p-2 bg-light rounded-2 border">
                              {h.service_details}
                            </div>
                          </div>
                        )}

                        {h.comment && (
                          <div className="mt-2">
                            <label className="text-muted text-xs fw-600 d-block uppercase mb-1">Comment</label>
                            <div className="text-xs italic text-primary">
                              "{h.comment}"
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                }) : <div className="text-muted text-xs">No history recorded yet.</div>}
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-header bg-transparent border-0 pt-4 px-4">
              <h6 className="fw-700 mb-0">Billing Information</h6>
            </div>
            <div className="card-body p-4">
              <div className="mb-4">
                <label className="text-muted text-xs fw-600 mb-1 d-block uppercase">TOTAL AMOUNT</label>
                <div className="fw-800 text-primary h4 mb-0">{formatCurrency(subscription.amount)}</div>
              </div>
              <div className="mb-3">
                <label className="text-muted text-xs fw-600 mb-2 d-block uppercase">ATTACHED QUOTATIONS</label>
                <div className="d-flex flex-column gap-2">
                  {subscription.quotations?.map((qId, idx) => (
                    <div key={idx} className="p-2 border rounded-3 d-flex justify-content-between align-items-center bg-light cursor-pointer" onClick={() => navigate(`/billing/quotes/view/${qId}`)}>
                      <div className="d-flex align-items-center">
                        <i className="bi bi-file-earmark-text text-primary me-2"></i>
                        <span className="text-sm fw-600 text-primary">View Quote {idx + 1}</span>
                      </div>
                      <i className="bi bi-chevron-right text-muted small"></i>
                    </div>
                  ))}
                  {(!subscription.quotations || subscription.quotations.length === 0) && (
                    <div className="text-muted text-xs">No quotations attached.</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="card border-0 shadow-sm">
            <div className="card-header bg-transparent border-0 pt-4 px-4">
              <h6 className="fw-700 mb-0">Comments</h6>
            </div>
            <div className="card-body p-4">
              {subscription.comments?.length > 0 ? subscription.comments.slice().reverse().map((c, i) => (
                <div key={i} className="mb-3 p-3 bg-light rounded-3">
                  <div className="d-flex justify-content-between mb-1">
                    <span className="text-xs fw-700">{c.admin}</span>
                    <span className="text-xs text-muted">{formatDate(c.date)}</span>
                  </div>
                  <div className="text-xs">{c.text}</div>
                </div>
              )) : <div className="text-muted text-xs">No comments yet.</div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionView;
